import { activeCheckpointId } from '../core/checkpoints';
import { createRun, type LevelRun } from '../core/level-runner';
import { respawn } from '../core/respawn';
import { tick } from '../core/tick';
import type { BossDef } from '../data/boss';
import { assistLevel } from '../data/assist';
import { loadLevel, type ResolvedLevel } from '../data/load-level';
import { ASSIST } from '../data/tuning';
import { ActionQueue } from '../input/action-queue';
import type { Action } from '../input/actions';
import { createAutosave, type Autosave } from '../save/autosave';
import { createSaveStore, type SaveStore } from '../save/save-store';
import { isLevelId, type LevelId } from '../data/ids';
import type { SaveSlot, Settings } from '../save/schema';
import type { StorageAdapter } from '../save/storage-adapter';
import { createLevelTally } from './level-tally';
import type { Recount } from './recount';
import type { ScreenFlow } from './screen-flow';
import type { Telemetry } from './telemetry';

export interface GameOptions {
  adapter: StorageAdapter;
  flow: ScreenFlow;
  now: () => string;
  prefersReducedMotion: boolean;
  /** Permanent or one-off notices for the interface (damaged save, storage unavailable). */
  onNotice: (text: string) => void;
  /** A level run was created: the renderer builds its scene for it (issue #53). */
  onRun?: (run: LevelRun) => void;
  /** Around every fixed step: the renderer keeps the previous state and animates La Bolsa (issue #66). */
  onBeforeStep?: (run: LevelRun) => void;
  onAfterStep?: (run: LevelRun) => void;
  /** The settings changed (a volume in AJUSTES, a save loaded again): the audio follows them (issue #75). */
  onSettings?: (settings: Settings) => void;
  /** Seed of a deployed run (GDD §15.4); the simulation stays deterministic for that seed. */
  newSeed?: () => number;
  /** The fixed-step loop, frozen while PAUSA (and the AJUSTES opened from it) is on screen (issue #60). */
  loop?: { setPaused(paused: boolean): void };
  /** Local playtesting telemetry (issue #144); no-op when absent. */
  telemetry?: Telemetry;
}

/** Level states in which the flight can be paused (GDD §13.2): also the US sky and the jump zone (#92); never during DERRIBO or after the end. */
const PAUSABLE = new Set(['INTRO', 'VUELO', 'REAPARICION', 'CIELO_EEUU', 'SALTO']);

/** What a deployment resumes from the slot (GDD §6.3): lives, bills and score saved with the checkpoint. */
export type RunProgress = Pick<SaveSlot['current'], 'lives' | 'dollars' | 'score' | 'scoreAtLevelStart' | 'cleanRun'>;

/**
 * The orchestrator in memory: the action queue shared by the DOM and the test hooks, the level run, the
 * save store and the screen. No DOM: main.ts binds it to the browser.
 */
export interface Game {
  readonly actions: ActionQueue;
  readonly flow: ScreenFlow;
  readonly run: LevelRun | null;
  readonly seed: number | null;
  readonly store: SaveStore;
  readonly adapter: StorageAdapter;
  /** One fixed step of the simulation, fed with the actions pressed since the previous one. */
  step(): void;
  /** Loads a level (or one of its checkpoints, waiting in REAPARICION as after a respawn) and shows VUELO. */
  /** `level` loads an already resolved level instead of the one registered as `id` (test fixtures). */
  /** `progress` restores what the slot saved with the checkpoint (GDD §6.3). */
  loadLevel(
    id: string,
    options: { seed: number; checkpointId?: string | null; level?: ResolvedLevel; progress?: RunProgress; boss?: BossDef },
  ): LevelRun;
  /**
   * "Confirmar" in BRIEFING (issue #57): flies the level of the active slot from its saved checkpoint, with
   * the saved lives and score, when it is the level in progress; any other level starts from the beginning.
   */
  deploy(levelId: LevelId): LevelRun;
  /** Reads the save document again from storage, through the whole load path (corrupt copy included). */
  reloadSave(): void;
  /** Changes one setting, writes the document once and tells the audio and the renderer. */
  updateSetting<K extends keyof Settings>(key: K, value: Settings[K]): void;
  /** The recount of the last completed level, for RECUENTO (issue #62). */
  readonly recount: Recount | null;
  /** The level score of the last LEVEL_COMPLETED, as the core computed it. */
  readonly lastLevelScore: number | null;
  /** Whether the flight is paused (PAUSA, or AJUSTES opened from it). */
  readonly paused: boolean;
  /**
   * Action sink of the DOM input (GDD §5.5): PAUSE toggles the pause on VUELO and PAUSA; FLAP is queued
   * only while flying, so a flap pressed in the pause is never executed.
   */
  input(action: Action): void;
  /** Opens PAUSA and freezes the loop; ignored outside VUELO or in DERRIBO and the end of the level. */
  pause(): void;
  /** "Reanudar": drops the actions pressed in the pause, restarts the loop and goes back to VUELO. */
  resume(): void;
  /** "Reiniciar desde el checkpoint": deploys the level again from what the slot saved, without a life lost. */
  restartFromCheckpoint(): void;
  /** Abandoning the mission, confirmed: the run is dropped and the save is not touched (GDD §6.3). */
  abandon(): void;
  /** "Ajustes" from PAUSA: the loop stays frozen until the pilot resumes. */
  openSettings(): void;
}

export function createGame(options: GameOptions): Game {
  const actions = new ActionQueue();
  let run: LevelRun | null = null;
  let seed: number | null = null;

  const openStore = () => {
    const store = createSaveStore({
      adapter: options.adapter,
      now: options.now,
      prefersReducedMotion: options.prefersReducedMotion,
    });
    if (store.notice) options.onNotice(store.notice);
    return store;
  };
  let store = openStore();
  let paused = false;
  let recount: Recount | null = null;
  let lastLevelScore: number | null = null;
  // Event-driven autosave of GDD §6.4 (#47), on the events of the run in flight.
  const autosaveFor = (current: SaveStore): Autosave => createAutosave({ store: current, getRunState: () => run?.state, now: options.now });
  let autosave = autosaveFor(store);

  const setPaused = (next: boolean) => {
    paused = next;
    if (!next) actions.drain();
    options.loop?.setPaused(next);
  };

  return {
    actions,
    flow: options.flow,
    get run() {
      return run;
    },
    get seed() {
      return seed;
    },
    get store() {
      return store;
    },
    adapter: options.adapter,
    step() {
      const drained = actions.drain();
      if (!run) return;
      options.onBeforeStep?.(run);
      tick(run.core, drained);
      options.onAfterStep?.(run);
    },
    loadLevel(id, { seed: levelSeed, checkpointId = null, level: given, progress, boss }) {
      // The assist mode is read here, at the load of the level, never in the middle of one (GDD §13.6, #138).
      const assisted = store.save.settings.assistMode;
      const base = given ?? loadLevel(id);
      const level = assisted ? assistLevel(base) : base;
      // The run starts from the progress of the active slot, so the autosave writes it back unchanged.
      const slot = store.save.lastSlotId ? store.save.slots[store.save.lastSlotId] : null;
      const next = createRun(level, {
        seed: levelSeed,
        ...(boss && { boss }),
        state: {
          ...(slot && { unlockedLevels: [...slot.unlockedLevels], completedLevels: structuredClone(slot.completedLevels) }),
          ...(progress && { scoreAtLevelStart: progress.scoreAtLevelStart, cleanRun: progress.cleanRun }),
          assisted,
        },
      });
      // A level with no saved progress starts with the lives of the mode: 3, or 5 with the assist mode.
      if (assisted && !progress) next.core.lives = ASSIST.startLives;
      if (checkpointId) {
        const track = next.checkpoints;
        const index = track.checkpoints.findIndex((checkpoint) => checkpoint.id === checkpointId);
        if (index < 0) throw new Error(`Unknown checkpoint "${checkpointId}" in "${id}"`);
        track.checkpoints.forEach((checkpoint, at) => {
          checkpoint.active = at <= index;
          if (at === index) checkpoint.flare = 'green';
        });
        track.activeIndex = index;
        next.state.checkpointId = activeCheckpointId(track);
        respawn(next);
        next.core.levelState = 'REAPARICION';
        next.observedState = 'REAPARICION';
      }
      if (progress) Object.assign(next.core, { lives: progress.lives, dollars: progress.dollars, score: progress.score });
      // The level state machine drives the exits of VUELO (OPERACION_FALLIDA, RECUENTO), #54.
      const tally = createLevelTally(next, (built) => (recount = built));
      next.core.listeners.push((event) => {
        tally(event);
        if (event.type === 'LEVEL_COMPLETED') lastLevelScore = Number(event.levelScore);
        autosave.onCoreEvent(event, next.core);
        options.telemetry?.onCoreEvent(event, next);
        if (event.type === 'LEVEL_STATE_CHANGED') options.flow.onLevelState(String(event.to));
      });
      options.telemetry?.levelStarted(next);
      if (paused) setPaused(false);
      actions.drain();
      run = next;
      seed = levelSeed;
      options.onRun?.(next);
      options.flow.goTo('VUELO', { levelId: next.level.def.id });
      return next;
    },
    deploy(levelId) {
      const save = store.save;
      const slot = save.lastSlotId ? save.slots[save.lastSlotId] : null;
      const current = slot?.current.levelId === levelId ? slot.current : null;
      return this.loadLevel(levelId, {
        seed: options.newSeed?.() ?? 1,
        checkpointId: current?.checkpointId ?? null,
        progress: current ?? undefined,
      });
    },
    get recount() {
      return recount;
    },
    get lastLevelScore() {
      return lastLevelScore;
    },
    get paused() {
      return paused;
    },
    input(action) {
      const screen = options.flow.current;
      if (action === 'PAUSE') {
        if (screen === 'VUELO') this.pause();
        else if (screen === 'PAUSA') this.resume();
        return;
      }
      if (screen === 'VUELO' && !paused) actions.enqueue(action);
    },
    pause() {
      if (paused || options.flow.current !== 'VUELO' || !run || !PAUSABLE.has(run.core.levelState)) return;
      setPaused(true);
      options.telemetry?.paused();
      options.flow.send('PAUSE');
    },
    resume() {
      if (!paused || options.flow.current !== 'PAUSA') return;
      setPaused(false);
      options.telemetry?.resumed();
      options.flow.send('RESUME');
    },
    restartFromCheckpoint() {
      if (!paused || !run || options.flow.current !== 'PAUSA') return;
      const id = run.level.def.id;
      // A level of the campaign goes back to what its slot saved; the test level or a level without a
      // slot goes back to its start.
      if (isLevelId(id)) this.deploy(id);
      else this.loadLevel(id, { seed: options.newSeed?.() ?? 1, level: run.level });
    },
    abandon() {
      if (!paused || options.flow.current !== 'PAUSA') return;
      if (run) options.telemetry?.abandoned(run);
      run = null;
      setPaused(false);
      options.flow.send('ABANDON');
    },
    openSettings() {
      if (paused && options.flow.current === 'PAUSA') options.flow.send('OPEN_SETTINGS');
    },
    reloadSave() {
      store = openStore();
      autosave = autosaveFor(store);
      options.onSettings?.(store.save.settings);
    },
    updateSetting(key, value) {
      store.updateSetting(key, value);
      options.onSettings?.(store.save.settings);
    },
  };
}
